/**
 * useTimelineData Hook
 *
 * Aggregator hook combining demand and supply data for the Sales Timeline.
 * Handles date range calculation, data merging, and strain aggregation.
 *
 * US-PE-006: Sales Timeline - Phase 1 Data Hooks
 */

import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/supabase-client'
import { createDate } from '@/utils/dateUtils'
import {
  getDateRangeForView,
  getDateRange,
  formatDateISO,
  isDateInPast,
  groupByWeek
} from '@/utils/timeline-utils'
import { useTimelineDemand } from './useTimelineDemand'
import { useTimelineSupply } from './useTimelineSupply'
import { useYieldSpreadCurve } from './useYieldSpreadCurve'

/**
 * Predefined colours for strains in the stacked chart
 * Using hex values for consistent rendering in charts and legends
 * @constant {Array}
 */
const STRAIN_COLOURS = [
  '#16a34a',  // Green (Odin primary)
  '#f97316',  // Orange
  '#3b82f6',  // Blue
  '#8b5cf6',  // Purple
  '#14b8a6',  // Teal
  '#ec4899',  // Pink
  '#eab308',  // Yellow
  '#06b6d4',  // Cyan
  '#6366f1',  // Indigo
  '#10b981'   // Emerald
]

/**
 * Hook to fetch and aggregate timeline data for demand vs supply visualisation
 *
 * @param {Object} params - Configuration parameters
 * @param {Date|string} params.anchorDate - Centre date for the view (typically today)
 * @param {string} params.viewMode - One of '7d', '14d', 'month'
 * @param {string|null} params.strainId - Strain ID filter (null for all strains aggregate)
 * @returns {Object} {
 *   data: Array - Daily aggregated data for charting,
 *   weeklyData: Array - Data grouped by week,
 *   strains: Array - List of strains with colours,
 *   dateRange: Object - { start, end, weekCount },
 *   isLoading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }
 */
export function useTimelineData({ anchorDate, viewMode = '7d', strainId = null }) {
  const [strains, setStrains] = useState([])
  const [strainsLoading, setStrainsLoading] = useState(true)
  const [strainsError, setStrainsError] = useState(null)

  // Calculate date range from anchor date and view mode
  const dateRange = useMemo(() => {
    const anchor = anchorDate
      ? (typeof anchorDate === 'string' ? createDate(anchorDate) : anchorDate)
      : createDate()
    return getDateRangeForView(anchor, viewMode)
  }, [anchorDate, viewMode])

  // Get yield curve for the selected strain (or default for all strains)
  const {
    curve: yieldCurve,
    isDefault: isDefaultCurve,
    loading: curveLoading
  } = useYieldSpreadCurve(strainId)

  // Fetch demand data
  const {
    data: demandData,
    loading: demandLoading,
    error: demandError,
    refetch: refetchDemand
  } = useTimelineDemand({
    startDate: dateRange.start,
    endDate: dateRange.end,
    strainId
  })

  // Fetch supply data with yield curve
  const {
    data: supplyData,
    loading: supplyLoading,
    error: supplyError,
    refetch: refetchSupply
  } = useTimelineSupply({
    startDate: dateRange.start,
    endDate: dateRange.end,
    strainId,
    yieldCurve
  })

  // Fetch all strains for colour mapping
  useEffect(() => {
    async function fetchStrains() {
      setStrainsLoading(true)
      setStrainsError(null)

      try {
        const { data, error } = await supabase
          .from('strains')
          .select('id, name, code')
          .eq('active', true)
          .order('name')

        if (error) throw error

        // Assign colours to strains
        const strainsWithColours = (data || []).map((strain, index) => ({
          ...strain,
          color: STRAIN_COLOURS[index % STRAIN_COLOURS.length]
        }))

        setStrains(strainsWithColours)
      } catch (err) {
        console.error('[useTimelineData] Error fetching strains:', err)
        setStrainsError(err.message)
        setStrains([])
      } finally {
        setStrainsLoading(false)
      }
    }

    fetchStrains()
  }, [])

  // Extract strains from supply data and merge with database strains
  // This ensures mock/demo supply data gets proper colour assignments
  const effectiveStrains = useMemo(() => {
    if (!supplyData || supplyData.length === 0) return strains

    // Get unique strain names from supply data
    const supplyStrainMap = new Map()
    supplyData.forEach(s => {
      if (!supplyStrainMap.has(s.strainName)) {
        supplyStrainMap.set(s.strainName, {
          id: s.strainId,
          name: s.strainName,
          isMock: s.isMock || false
        })
      }
    })

    // Merge with database strains, preserving database info where available
    const merged = []
    const usedDbStrains = new Set()

    supplyStrainMap.forEach((supplyStrain) => {
      const dbStrain = strains.find(s => s.name === supplyStrain.name)
      if (dbStrain) {
        merged.push(dbStrain)
        usedDbStrains.add(dbStrain.id)
      } else {
        // Supply strain not in database - assign colour based on position
        merged.push({
          ...supplyStrain,
          color: STRAIN_COLOURS[merged.length % STRAIN_COLOURS.length]
        })
      }
    })

    return merged
  }, [supplyData, strains])

  // Aggregate and merge demand/supply data
  const aggregatedData = useMemo(() => {
    if (!demandData || !supplyData) return []

    // Generate all dates in range
    const start = formatDateISO(dateRange.start)
    const end = formatDateISO(dateRange.end)
    const allDates = getDateRange(start, end)
    const today = formatDateISO(createDate())

    // Create strain colour map from effective strains (includes mock strains with colours)
    const strainColorMap = new Map()
    effectiveStrains.forEach(s => strainColorMap.set(s.name, s))

    // Build daily aggregate map
    const dailyMap = new Map()

    // Initialise all dates
    allDates.forEach(date => {
      dailyMap.set(date, {
        date,
        demandKg: 0,
        supplyByStrain: {},
        batchesByStrain: {},  // Track batch breakdown per strain
        totalSupplyKg: 0,
        isActual: isDateInPast(date) || date === today,
        demandIsActual: isDateInPast(date) || date === today,
        supplyIsActual: isDateInPast(date) || date === today
      })
    })

    // Aggregate demand by date
    demandData.forEach(d => {
      const entry = dailyMap.get(d.date)
      if (entry) {
        entry.demandKg += d.demandKg
        entry.demandIsActual = entry.demandIsActual && d.isActual
      }
    })

    // Aggregate supply by date and strain, tracking batch breakdown
    supplyData.forEach(s => {
      const entry = dailyMap.get(s.date)
      if (entry) {
        // Add to strain-specific supply
        const strainKey = s.strainName
        entry.supplyByStrain[strainKey] = (entry.supplyByStrain[strainKey] || 0) + s.supplyKg
        entry.totalSupplyKg += s.supplyKg
        entry.supplyIsActual = entry.supplyIsActual && s.isActual

        // Track batch breakdown for this strain
        if (!entry.batchesByStrain[strainKey]) {
          entry.batchesByStrain[strainKey] = []
        }
        // Find existing batch entry or create new one
        const existingBatch = entry.batchesByStrain[strainKey].find(b => b.code === s.batchCode)
        if (existingBatch) {
          existingBatch.kg += s.supplyKg
        } else {
          entry.batchesByStrain[strainKey].push({
            code: s.batchCode,
            kg: s.supplyKg
          })
        }

        // Add strain colour if not already present
        if (!entry.supplyByStrain[`${strainKey}_color`]) {
          const strainInfo = strainColorMap.get(strainKey)
          if (strainInfo) {
            entry.supplyByStrain[`${strainKey}_color`] = strainInfo.color
          }
        }
      }
    })

    // Calculate gap (supply - demand) and determine overall isActual flag
    dailyMap.forEach(entry => {
      entry.gapKg = entry.totalSupplyKg - entry.demandKg
      entry.isActual = entry.demandIsActual && entry.supplyIsActual
    })

    // Convert to array, sorted by date
    return Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [demandData, supplyData, dateRange, effectiveStrains])

  // Group data by week for multi-week views
  const weeklyData = useMemo(() => {
    return groupByWeek(aggregatedData)
  }, [aggregatedData])

  // Combined loading state
  const isLoading = demandLoading || supplyLoading || strainsLoading || curveLoading

  // Combined error (first error found)
  const error = demandError || supplyError || strainsError

  // Refetch all data
  const refetch = () => {
    refetchDemand()
    refetchSupply()
  }

  return {
    data: aggregatedData,
    weeklyData,
    strains: effectiveStrains,
    dateRange: {
      start: formatDateISO(dateRange.start),
      end: formatDateISO(dateRange.end),
      weekCount: dateRange.weekCount
    },
    yieldCurve: {
      curve: yieldCurve,
      isDefault: isDefaultCurve
    },
    isLoading,
    error,
    refetch
  }
}

/**
 * Extract unique strains from supply data with their totals
 *
 * @param {Array} supplyData - Supply records
 * @param {Array} strainsWithColours - Strains with colour assignments
 * @returns {Array} Array of strain summaries with totals
 */
export function extractStrainSummaries(supplyData, strainsWithColours) {
  const strainTotals = new Map()

  supplyData.forEach(s => {
    const existing = strainTotals.get(s.strainName) || {
      strainName: s.strainName,
      strainId: s.strainId,
      totalKg: 0,
      recordCount: 0
    }
    existing.totalKg += s.supplyKg
    existing.recordCount += 1
    strainTotals.set(s.strainName, existing)
  })

  // Add colours and sort by total
  return Array.from(strainTotals.values())
    .map(summary => {
      const strainInfo = strainsWithColours.find(s => s.name === summary.strainName)
      return {
        ...summary,
        color: strainInfo?.color || STRAIN_COLOURS[0]
      }
    })
    .sort((a, b) => b.totalKg - a.totalKg)
}

/**
 * Calculate summary statistics for the timeline data
 *
 * @param {Array} data - Aggregated daily timeline data
 * @returns {Object} Summary statistics
 */
export function calculateTimelineSummary(data) {
  if (!data || data.length === 0) {
    return {
      totalDemandKg: 0,
      totalSupplyKg: 0,
      totalGapKg: 0,
      avgDailyDemandKg: 0,
      avgDailySupplyKg: 0,
      daysWithShortfall: 0,
      daysWithSurplus: 0,
      maxShortfallKg: 0,
      maxSurplusKg: 0
    }
  }

  const totalDemandKg = data.reduce((sum, d) => sum + d.demandKg, 0)
  const totalSupplyKg = data.reduce((sum, d) => sum + d.totalSupplyKg, 0)
  const totalGapKg = totalSupplyKg - totalDemandKg

  const daysWithShortfall = data.filter(d => d.gapKg < 0).length
  const daysWithSurplus = data.filter(d => d.gapKg > 0).length

  const gaps = data.map(d => d.gapKg)
  const maxShortfallKg = Math.min(0, ...gaps)
  const maxSurplusKg = Math.max(0, ...gaps)

  return {
    totalDemandKg: Math.round(totalDemandKg * 100) / 100,
    totalSupplyKg: Math.round(totalSupplyKg * 100) / 100,
    totalGapKg: Math.round(totalGapKg * 100) / 100,
    avgDailyDemandKg: Math.round((totalDemandKg / data.length) * 100) / 100,
    avgDailySupplyKg: Math.round((totalSupplyKg / data.length) * 100) / 100,
    daysWithShortfall,
    daysWithSurplus,
    maxShortfallKg: Math.round(maxShortfallKg * 100) / 100,
    maxSurplusKg: Math.round(maxSurplusKg * 100) / 100
  }
}

export default useTimelineData
